import React from 'react';
import { motion } from 'framer-motion';
import { Shield } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import AnimatedLogo from '../components/AnimatedLogo';

const Login = () => {
  const { t } = useLanguage();
  const searchParams = new URLSearchParams(window.location.search);
  const redirectTo = searchParams.get('redirectTo');
  const isInvitation = redirectTo?.includes('accept-invitation');

  const handleLogin = () => {
    const prompt = searchParams.get('prompt');
    const baseUrl = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:5000'}/api/auth/google`;
    
    // Construct the redirect URL with all necessary parameters
    let authUrl = baseUrl;
    const params = new URLSearchParams();
    if (redirectTo) params.set('redirectTo', redirectTo);
    if (prompt) params.set('prompt', prompt);
    
    const queryString = params.toString();
    if (queryString) authUrl += `?${queryString}`;

    window.location.href = authUrl;
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6 relative overflow-hidden">
      {/* Premium Background Pattern */}
      <div className="absolute inset-0 opacity-[0.4] pointer-events-none" 
           style={{ 
             backgroundImage: `radial-gradient(circle at 2px 2px, rgba(255,255,255,0.05) 1px, transparent 0)`,
             backgroundSize: '32px 32px' 
           }} 
      />
      
      {/* Background Blobs */}
      <motion.div 
        animate={{ 
          scale: [1, 1.2, 1],
          x: [0, 50, 0],
          y: [0, -30, 0]
        }}
        transition={{ duration: 15, repeat: Infinity, ease: "easeInOut" }}
        className="absolute top-[-10%] left-[-10%] w-[500px] h-[500px] bg-primary/20 rounded-full blur-[120px] pointer-events-none" 
      />
      <motion.div 
        animate={{ 
          scale: [1, 1.3, 1],
          x: [0, -50, 0],
          y: [0, 50, 0]
        }}
        transition={{ duration: 18, repeat: Infinity, ease: "easeInOut", delay: 2 }}
        className="absolute bottom-[-10%] right-[-10%] w-[500px] h-[500px] bg-secondary/20 rounded-full blur-[120px] pointer-events-none" 
      />

      {/* Login Card */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: "easeOut" }}
        className="w-full max-w-md relative z-10"
      >
        <div className="glass-card p-10 shadow-[0_0_50px_rgba(37,99,235,0.1)] border-white/20">
          <div className="flex flex-col items-center text-center mb-8">
            <AnimatedLogo size="text-6xl" showIcon={false} />
            <p className="text-slate-400 font-medium mb-10 italic">
              {isInvitation ? "Accept your invitation to join the team" : t('loginTagline')}
            </p>

            <h2 className="text-xl font-bold mb-8 text-slate-100">
               {isInvitation ? "Login to Join Project" : t('loginTitle')}
            </h2>

            <button
              onClick={handleLogin}
              className="w-full flex items-center justify-center gap-4 py-4 px-6 rounded-2xl bg-white/5 border border-white/10 hover:bg-white/10 transition-all hover:scale-[1.02] active:scale-[0.98] group relative overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-primary/10 to-transparent translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-700" />
              <img src="https://www.gstatic.com/images/branding/product/1x/gsa_512dp.png" className="w-6 h-6" alt="Google" />
              <span className="font-semibold text-slate-200">{t('continueWithGoogle')}</span>
            </button>

            <div className="mt-10 pt-8 border-t border-white/5 w-full flex flex-col items-center">
               <div className="flex items-center gap-2 text-cyan-500/80 mb-2">
                  <Shield size={16} />
                  <span className="text-xs font-bold uppercase tracking-widest">{t('safeSecure')}</span>
               </div>
               <p className="text-[10px] text-slate-500 max-w-[200px]">
                 {t('loginSecurityDesc')}
               </p>
            </div>
          </div>
        </div>


      </motion.div>
    </div>
  );
};

export default Login;
